// src/store/slices/orderManagementSlice.js - COMPLETE VERSION
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/api/axios';

const API_ROUTES = {
  GET_ALL_ORDERS: '/order-management',
  GET_ORDER: '/order-management',
  UPDATE_STATUS: '/order-management',
  UPDATE_PAYMENT: '/order-management',
  UPDATE_SHIPPING: '/order-management',
  PROCESS_REFUND: '/order-management',
  BULK_UPDATE: '/order-management/bulk-update',
  GET_STATS: '/order-management/stats',
  EXPORT_ORDERS: '/order-management/export'
};

export const getAllOrders = createAsyncThunk(
  'orderManagement/getAllOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        limit = 10,
        status = '',
        paymentStatus = '',
        paymentMethod = '',
        search = '',
        dateFrom = '',
        dateTo = '',
        sortBy = '',
        sortOrder = ''
      } = params;

      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);

      if (status) queryParams.append('status', status);
      if (paymentStatus) queryParams.append('paymentStatus', paymentStatus);
      if (paymentMethod) queryParams.append('paymentMethod', paymentMethod);
      if (search) queryParams.append('search', search);
      if (dateFrom) queryParams.append('dateFrom', dateFrom);
      if (dateTo) queryParams.append('dateTo', dateTo);
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);

      const response = await axiosInstance.get(`${API_ROUTES.GET_ALL_ORDERS}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch orders');
    }
  }
);

export const getOrderById = createAsyncThunk(
  'orderManagement/getOrderById',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_ROUTES.GET_ORDER}/${orderId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order details');
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orderManagement/updateOrderStatus',
  async ({ orderId, status, note }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ROUTES.UPDATE_STATUS}/${orderId}/status`,
        { status, note }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update order status');
    }
  }
);

export const updatePaymentStatus = createAsyncThunk(
  'orderManagement/updatePaymentStatus',
  async ({ orderId, paymentStatus, razorpayPaymentId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ROUTES.UPDATE_PAYMENT}/${orderId}/payment`,
        { paymentStatus, razorpayPaymentId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update payment status');
    }
  }
);

export const updateShippingDetails = createAsyncThunk(
  'orderManagement/updateShippingDetails',
  async ({ orderId, trackingNumber, carrier, estimatedDelivery }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ROUTES.UPDATE_SHIPPING}/${orderId}/shipping`,
        { trackingNumber, carrier, estimatedDelivery }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update shipping details');
    }
  }
);

export const processRefund = createAsyncThunk(
  'orderManagement/processRefund',
  async ({ orderId, refundAmount, reason }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${API_ROUTES.PROCESS_REFUND}/${orderId}/refund`,
        { refundAmount, reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to process refund');
    }
  }
);

export const bulkUpdateStatus = createAsyncThunk(
  'orderManagement/bulkUpdateStatus',
  async ({ orderIds, status, note }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_ROUTES.BULK_UPDATE,
        { orderIds, status, note }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk update orders');
    }
  }
);

export const getOrderStats = createAsyncThunk(
  'orderManagement/getOrderStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ROUTES.GET_STATS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order statistics');
    }
  }
);

export const exportOrders = createAsyncThunk(
  'orderManagement/exportOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { status, dateFrom, dateTo, paymentStatus } = params;
      const queryParams = new URLSearchParams();

      if (status) queryParams.append('status', status);
      if (dateFrom) queryParams.append('dateFrom', dateFrom);
      if (dateTo) queryParams.append('dateTo', dateTo);
      if (paymentStatus) queryParams.append('paymentStatus', paymentStatus);

      const response = await axiosInstance.get(`${API_ROUTES.EXPORT_ORDERS}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export orders');
    }
  }
);

const initialState = {
  orders: [],
  currentOrder: null,
  stats: null,
  exportedOrders: [],
  pagination: null,
  loading: false,
  error: null,
  success: false,
  lastUpdateTimestamp: null
};

const orderManagementSlice = createSlice({
  name: 'orderManagement',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    resetOrderState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data.orders;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(getAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch orders';
      })
      .addCase(getOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload.data.order;
      })
      .addCase(getOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch order details';
      })
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastUpdateTimestamp = Date.now();
        const { orderId, status } = action.payload.data;
        state.orders = state.orders.map(order =>
          order.orderId === orderId ? { ...order, status } : order
        );
        if (state.currentOrder?.orderId === orderId) {
          state.currentOrder = { ...state.currentOrder, status };
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update order status';
      })
      .addCase(updatePaymentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updatePaymentStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastUpdateTimestamp = Date.now();
        const { orderId, paymentStatus } = action.payload.data;
        state.orders = state.orders.map(order =>
          order.orderId === orderId
            ? { ...order, payment: { ...order.payment, status: paymentStatus } }
            : order
        );
        if (state.currentOrder?.orderId === orderId) {
          state.currentOrder = {
            ...state.currentOrder,
            payment: { ...state.currentOrder.payment, status: paymentStatus }
          };
        }
      })
      .addCase(updatePaymentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update payment status';
      })
      .addCase(updateShippingDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateShippingDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastUpdateTimestamp = Date.now();
        const { orderId, shipping, estimatedDelivery } = action.payload.data;
        if (state.currentOrder?.orderId === orderId) {
          state.currentOrder = {
            ...state.currentOrder,
            shipping,
            estimatedDelivery
          };
        }
      })
      .addCase(updateShippingDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update shipping details';
      })
      .addCase(processRefund.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(processRefund.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastUpdateTimestamp = Date.now();
        const { orderId, paymentStatus } = action.payload.data;
        state.orders = state.orders.map(order =>
          order.orderId === orderId
            ? { ...order, payment: { ...order.payment, status: paymentStatus } }
            : order
        );
        if (state.currentOrder?.orderId === orderId) {
          state.currentOrder = {
            ...state.currentOrder,
            payment: { ...state.currentOrder.payment, status: paymentStatus }
          };
        }
      })
      .addCase(processRefund.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to process refund';
      })
      .addCase(bulkUpdateStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(bulkUpdateStatus.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.lastUpdateTimestamp = Date.now();
      })
      .addCase(bulkUpdateStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to bulk update orders';
      })
      .addCase(getOrderStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.data.stats;
      })
      .addCase(getOrderStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch order statistics';
      })
      .addCase(exportOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.exportedOrders = action.payload.data.orders;
      })
      .addCase(exportOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to export orders';
      });
  }
});

export const { clearError, clearSuccess, clearCurrentOrder, resetOrderState } = orderManagementSlice.actions;
export default orderManagementSlice.reducer;